import { PDFDocument, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { getLayerDocument, Layer, PDFFileList } from '../api';
import { cachePdfBlob, getCachedPdfBlob } from './offlineStore';

export type BulkDownloadProgress = {
  total: number;
  completed: number;
  currentFilename: string | null;
  phase: 'idle' | 'fetching' | 'flattening' | 'saving' | 'done' | 'error';
};

type ProgressFn = (next: BulkDownloadProgress) => void;

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to grab the blob before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const fetchPdfBlob = async (pdf: PDFFileList): Promise<Blob> => {
  try {
    // No credentials: pdf.file_url is a cross-origin signed GCS URL.
    // Sending credentials would require non-wildcard Access-Control-Allow-Origin,
    // and the signed URL itself is the access credential.
    const response = await fetch(pdf.file_url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    await cachePdfBlob(pdf.id, pdf.original_filename, blob).catch(() => undefined);
    return blob;
  } catch (networkError) {
    const cached = await getCachedPdfBlob(pdf.id);
    if (cached) return cached.blob;
    throw networkError;
  }
};

const downloadFilename = (pdf: PDFFileList, suffix?: string): string => {
  const base = (pdf.original_filename || `drawing-${pdf.id}.pdf`).replace(/\.pdf$/i, '');
  return suffix ? `${base}${suffix}.pdf` : `${base}.pdf`;
};

/**
 * Bulk download original PDFs without any markup overlays.
 * Downloads are triggered sequentially to avoid spamming the browser.
 */
export const bulkDownloadOriginals = async (
  pdfs: PDFFileList[],
  onProgress: ProgressFn = () => undefined
): Promise<{ succeeded: number; failed: Array<{ id: string; filename: string; error: string }> }> => {
  const total = pdfs.length;
  const failed: Array<{ id: string; filename: string; error: string }> = [];
  let completed = 0;

  for (const pdf of pdfs) {
    onProgress({ total, completed, currentFilename: pdf.original_filename, phase: 'fetching' });
    try {
      const blob = await fetchPdfBlob(pdf);
      triggerBlobDownload(blob, downloadFilename(pdf));
      completed += 1;
      onProgress({ total, completed, currentFilename: pdf.original_filename, phase: 'saving' });
    } catch (error) {
      failed.push({
        id: pdf.id,
        filename: pdf.original_filename,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  onProgress({ total, completed, currentFilename: null, phase: 'done' });
  return { succeeded: completed, failed };
};

// ---------------------------------------------------------------------------
// Markup flattening
// ---------------------------------------------------------------------------

const CLOUD_STROKE = rgb(0.84, 0.13, 0.18);
const ARROW_STROKE = rgb(0.07, 0.42, 0.89);
const PIN_FILL = rgb(0.84, 0.13, 0.18);
const TEXT_COLOR = rgb(0.1, 0.1, 0.1);
const TEXTBOX_BG = rgb(1, 0.97, 0.78);
const TEXTBOX_STROKE = rgb(0.65, 0.55, 0.1);

const drawScallopedRect = (
  page: PDFPage,
  x: number,
  y: number,
  width: number,
  height: number,
  amplitude: number,
) => {
  // Render the cloud as the bounding rect plus simple scalloped arcs along each edge.
  // Simplified compared to the viewer cloud, but visually conveys the markup.
  page.drawRectangle({
    x,
    y,
    width,
    height,
    borderColor: CLOUD_STROKE,
    borderWidth: 1.5,
    color: undefined,
    opacity: 1,
  });
  // Top/bottom scallops drawn as small circles to suggest the cloud edge.
  const radius = Math.max(4, amplitude);
  const step = radius * 2;
  for (let cx = x + radius; cx <= x + width - radius; cx += step) {
    page.drawCircle({ x: cx, y: y + height, size: radius, borderColor: CLOUD_STROKE, borderWidth: 1 });
    page.drawCircle({ x: cx, y, size: radius, borderColor: CLOUD_STROKE, borderWidth: 1 });
  }
  for (let cy = y + radius; cy <= y + height - radius; cy += step) {
    page.drawCircle({ x: x + width, y: cy, size: radius, borderColor: CLOUD_STROKE, borderWidth: 1 });
    page.drawCircle({ x, y: cy, size: radius, borderColor: CLOUD_STROKE, borderWidth: 1 });
  }
};

const drawArrowOnPage = (page: PDFPage, x1: number, y1: number, x2: number, y2: number) => {
  page.drawLine({
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
    thickness: 1.6,
    color: ARROW_STROKE,
  });
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const headLen = 10;
  const wing = Math.PI / 7;
  const hx1 = x2 - headLen * Math.cos(angle - wing);
  const hy1 = y2 - headLen * Math.sin(angle - wing);
  const hx2 = x2 - headLen * Math.cos(angle + wing);
  const hy2 = y2 - headLen * Math.sin(angle + wing);
  page.drawLine({ start: { x: x2, y: y2 }, end: { x: hx1, y: hy1 }, thickness: 1.6, color: ARROW_STROKE });
  page.drawLine({ start: { x: x2, y: y2 }, end: { x: hx2, y: hy2 }, thickness: 1.6, color: ARROW_STROKE });
};

const drawTextboxOnPage = async (
  outputPdf: PDFDocument,
  page: PDFPage,
  text: string,
  fontSize: number,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  page.drawRectangle({
    x,
    y,
    width,
    height,
    color: TEXTBOX_BG,
    borderColor: TEXTBOX_STROKE,
    borderWidth: 0.75,
    opacity: 0.95,
  });
  if (!text) return;
  const font = await outputPdf.embedFont(StandardFonts.Helvetica);
  const size = Math.max(8, Math.min(48, fontSize || 12));
  const lineHeight = size * 1.25;
  const words = text.split(/(\s+)/);
  const lines: string[] = [];
  let currentLine = '';
  for (const word of words) {
    const candidate = currentLine + word;
    if (font.widthOfTextAtSize(candidate, size) > width - 6 && currentLine) {
      lines.push(currentLine.trimEnd());
      currentLine = word.trimStart();
    } else {
      currentLine = candidate;
    }
  }
  if (currentLine) lines.push(currentLine.trimEnd());

  const maxLines = Math.max(1, Math.floor((height - 4) / lineHeight));
  const visibleLines = lines.slice(0, maxLines);
  visibleLines.forEach((line, idx) => {
    page.drawText(line, {
      x: x + 4,
      y: y + height - 4 - (idx + 1) * lineHeight + lineHeight * 0.2,
      size,
      font,
      color: TEXT_COLOR,
    });
  });
};

const drawPinOnPage = (page: PDFPage, x: number, y: number, size: number) => {
  // Simple lollipop pin: filled red circle with a thin stem.
  const radius = Math.max(4, size / 3);
  page.drawCircle({
    x,
    y: y + radius,
    size: radius,
    color: PIN_FILL,
    borderColor: rgb(0.6, 0.05, 0.08),
    borderWidth: 0.75,
  });
  page.drawLine({
    start: { x, y },
    end: { x, y: y + radius },
    thickness: 1,
    color: PIN_FILL,
  });
};

const drawLayerOnPage = async (
  outputPdf: PDFDocument,
  page: PDFPage,
  layer: Layer,
) => {
  const data = (layer.data ?? {}) as Record<string, unknown>;
  const { height: pageHeight } = page.getSize();
  const flipY = (value: number) => pageHeight - value;

  if (layer.layer_type === 'shape') {
    const shapeType = String(data.shapeType ?? 'cloud');
    if (shapeType === 'cloud') {
      const x = Number(data.x ?? 0);
      const y = Number(data.y ?? 0);
      const width = Math.max(8, Number(data.width ?? 0));
      const height = Math.max(8, Number(data.height ?? 0));
      drawScallopedRect(page, x, flipY(y + height), width, height, 6);
      return;
    }
    if (shapeType === 'arrow') {
      const x1 = Number(data.x1 ?? 0);
      const y1 = Number(data.y1 ?? 0);
      const x2 = Number(data.x2 ?? 0);
      const y2 = Number(data.y2 ?? 0);
      drawArrowOnPage(page, x1, flipY(y1), x2, flipY(y2));
      return;
    }
    if (shapeType === 'pin') {
      const x = Number(data.x ?? 0);
      const y = Number(data.y ?? 0);
      const height = Math.max(16, Number(data.height ?? 24));
      drawPinOnPage(page, x + 12, flipY(y + height), height);
      return;
    }
  }

  if (layer.layer_type === 'text' || layer.layer_type === 'annotation') {
    const x = Number(data.x ?? 0);
    const y = Number(data.y ?? 0);
    const width = Math.max(40, Number(data.width ?? 120));
    const height = Math.max(20, Number(data.height ?? 28));
    const text = String(data.text ?? '');
    const fontSize = Number(data.fontSize ?? 14);
    await drawTextboxOnPage(outputPdf, page, text, fontSize, x, flipY(y + height), width, height);
  }
};

const fetchLayersForPdf = async (pdfId: string): Promise<Layer[]> => {
  try {
    const doc = await getLayerDocument(pdfId);
    return (doc.layers ?? []).filter((layer) => layer.visible !== false && layer.markup_scope !== 'personal');
  } catch (error) {
    console.warn(`Failed to load layer document for ${pdfId}`, error);
    return [];
  }
};

/**
 * Build a markup-flattened copy of one PDF using pdf-lib and trigger download.
 */
export const downloadPdfWithMarkup = async (pdf: PDFFileList): Promise<void> => {
  const [pdfBlob, layers] = await Promise.all([fetchPdfBlob(pdf), fetchLayersForPdf(pdf.id)]);
  const sourceBytes = new Uint8Array(await pdfBlob.arrayBuffer());
  const outputPdf = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
  const pages = outputPdf.getPages();

  const layersByPage = new Map<number, Layer[]>();
  for (const layer of layers) {
    const list = layersByPage.get(layer.page_number) ?? [];
    list.push(layer);
    layersByPage.set(layer.page_number, list);
  }
  for (const list of layersByPage.values()) {
    list.sort((a, b) => a.z_index - b.z_index);
  }

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const pageNumber = pageIndex + 1;
    const pageLayers = layersByPage.get(pageNumber) ?? [];
    if (!pageLayers.length) continue;
    const page = pages[pageIndex];
    for (const layer of pageLayers) {
      await drawLayerOnPage(outputPdf, page, layer);
    }
  }

  const outBytes = await outputPdf.save();
  triggerBlobDownload(new Blob([outBytes], { type: 'application/pdf' }), downloadFilename(pdf, '-markup'));
};

/**
 * Bulk download markup-flattened versions of multiple PDFs.
 * Each file is built independently and downloaded sequentially.
 */
export const bulkDownloadWithMarkup = async (
  pdfs: PDFFileList[],
  onProgress: ProgressFn = () => undefined
): Promise<{ succeeded: number; failed: Array<{ id: string; filename: string; error: string }> }> => {
  const total = pdfs.length;
  const failed: Array<{ id: string; filename: string; error: string }> = [];
  let completed = 0;

  for (const pdf of pdfs) {
    onProgress({ total, completed, currentFilename: pdf.original_filename, phase: 'flattening' });
    try {
      await downloadPdfWithMarkup(pdf);
      completed += 1;
      onProgress({ total, completed, currentFilename: pdf.original_filename, phase: 'saving' });
    } catch (error) {
      failed.push({
        id: pdf.id,
        filename: pdf.original_filename,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  onProgress({ total, completed, currentFilename: null, phase: 'done' });
  return { succeeded: completed, failed };
};
